import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';

const { Option } = Select;

interface SectorModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sector?: {
    id: number;
    descripcion: string;
    alta?: number;
  } | null;
}

const SectorModal: React.FC<SectorModalProps> = ({ visible, onClose, onSuccess, sector }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const isEditing = !!sector;

  // Cargar datos si es edición
  useEffect(() => {
    if (visible && sector) {
      form.setFieldsValue({
        descripcion: sector.descripcion,
        alta: sector.alta !== undefined ? sector.alta : 1
      });
    } else {
      form.resetFields();
    }
  }, [visible, sector, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const accessToken = await getAccessTokenSilently();
      const url = isEditing
        ? `${process.env.REACT_APP_INSUMOS_URL}/editar_sectores/${empresa}/${sucursal}/${sector!.id}`
        : `${process.env.REACT_APP_INSUMOS_URL}/crear_sectores`;

      const response = await fetch(url, {
        method: 'POST', // Siempre POST (tanto para crear como editar)
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(isEditing ? values : {
          ...values,
          id_empresa: empresa,
          id_sucursal: sucursal,
        }),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      message.success(`Sector ${isEditing ? 'actualizado' : 'creado'} correctamente`);
      form.resetFields();
      onSuccess(); // Recargar lista
      onClose(); // Cerrar modal
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el sector`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: theme.colors.primaryDark, 
          fontSize: theme.fontSizes.large,
          fontWeight: 600
        }}>
          {isEditing ? 'Editar Sector' : 'Nuevo Sector'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
      okButtonProps={{
        style: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          fontWeight: 500
        }
      }}
      cancelButtonProps={{
        style: {
          fontWeight: 500
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
        initialValues={{
          alta: 1 // Activo por defecto para nuevos sectores
        }}
      >
        <Form.Item 
          name="descripcion" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Descripción
            </span>
          }
          rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
        >
          <Input 
            maxLength={30} 
            placeholder="Ingrese la descripción del sector"
          />
        </Form.Item>

        <Form.Item 
          name="alta" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Estado
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
        >
          <Select placeholder="Seleccione el estado">
            <Option value={1}>Activo</Option>
            <Option value={0}>Inactivo</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SectorModal;

